import { Typography as T } from "@mui/material";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import { LiveChatWidget } from "@livechat/widget-react";
import { colors } from "../../styles";
import about from "../../assets/icons/about.svg";
import home from "../../assets/icons/home.svg";
import reading from "../../assets/icons/reading.svg";
import savings from "../../assets/icons/savings.svg";
import settings from "../../assets/icons/settings.svg";
import tifinEsa from "../../assets/logos/tifinEsa.svg";
import { ADMIN_SETTINGS } from "../../routes/paths";
import useGetMe from "../../hooks/queries/useGetMe";
import { useStoreActions, useStoreState } from "../../store";

type Props = {};

export const NavContainer = styled("div")({
  height: "100vh",
  borderRight: `1px solid ${colors.gray_01}`,
  minWidth: 240,
  width: 240,
  display: "flex",
  flexDirection: "column",
});

export const FirmLogo = styled("img")(`
  width: 100%;
  cursor: pointer;
  padding: 20px 20px 0 20px;
`);

export const MenuRow = styled("div")(`
  display: flex;
  height: 40px;
  margin: 10px;
  margin: 10px auto;
  cursor: pointer;
  background: white;
  &:hover {
    background: #DBF2FC;
  }
`);

export const MenuRowSelected = styled("div")(`
  height: 40px;
  display: flex;
  margin: 10px auto;
  cursor: pointer;
  background: white;
  z-index: 999;
  background: #DBF2FC;  
  border-radius: 4px;
`);

export const MenuSelected = styled("div")(`
  padding: 0px;
  height: 16px;
  margin: 12px 8px 0px 0px;
  cursor: pointer;
  border-left: 2px solid #457F8F;
  border-radius: 0px 2px 2px 0px;
`);

export const Divider = styled("div")(`
  border-top: 1px solid ${colors.gray_01};
  margin: 4px auto;
`);

export const RowIcon = styled("img")(`
  width: 24px;
  height: 24px;
  margin: 8px 8px 0 0;
`);

export const RowLabel = ({ children }: { children: string }) => {
  return (
    <T variant="smMed" sx={{ marginLeft: "4px", alignSelf: "center" }}>
      {children}
    </T>
  );
};

const menuOptions = {
  links: [
    {
      label: "Home",
      link: "",
      icon: home,
    },
    {
      label: "Programs",
      link: "programs",
      icon: about,
    },
    {
      label: "Employees",
      link: "employees",
      icon: reading,
    },
    {
      label: "Reports",
      link: "reports",
      icon: savings,
    },
    {
      label: "Profile & Settings",
      link: "settings",
      icon: settings,
      divider: true,
    },
  ],
};

const NavBar = (props: Props) => {
  const { data } = useGetMe();
  const [focusedSection, setFocusedSection] = useState<any>(null);
  const livechatActions = useStoreActions((actions) => actions.livechat);
  const livechatState = useStoreState((state) => state.livechat);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const splitPath = location.pathname.split("/admin/dashboard/");
    if (splitPath?.length === 1) {
      // home route
      setFocusedSection(menuOptions.links[0]);
      return;
    }
    const focusedOptionArray = menuOptions.links.filter((option) => {
      return splitPath?.[1] === option.link;
    });
    const newFocusedSection = focusedOptionArray?.[0];
    setFocusedSection(newFocusedSection);
  }, [location]);

  const goToPage = (row: any) => {
    setFocusedSection(row);
    navigate(row.link);
  };

  return (
    <NavContainer>
      <div className="flex flex-col" style={{ height: "95%" }}>
        <FirmLogo src={tifinEsa} onClick={() => navigate("/")} />
        {data ? (
          <>
            <div
              className="flex flex-col"
              style={{ height: "100%", width: "80%", margin: "0 auto" }}
            >
              <div>
                {menuOptions.links.map((row, index) => {
                  if (row === focusedSection) {
                    return (
                      <div key={index} style={{ width: "100%" }}>
                        <MenuRowSelected
                          key={index}
                          onClick={() => goToPage(row)}
                        >
                          <MenuSelected />
                          <RowIcon src={row.icon} />
                          <RowLabel>{row.label}</RowLabel>
                        </MenuRowSelected>
                        {index < menuOptions.links.length && <Divider />}
                      </div>
                    );
                  }
                  return (
                    <div key={index} style={{ width: "100%" }}>
                      <MenuRow key={index} onClick={() => goToPage(row)}>
                        <RowIcon src={row.icon} />
                        <RowLabel>{row.label}</RowLabel>
                      </MenuRow>
                      {index < menuOptions.links.length && <Divider />}
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-col" style={{ marginTop: "auto" }}>
                <div
                  className="flex flex-row mb-4"
                  style={{ alignItems: "flex-start", cursor: "pointer" }}
                  onClick={() => navigate(ADMIN_SETTINGS)}
                >
                  <AccountCircleOutlinedIcon
                    fontSize="small"
                    sx={{ alignSelf: "center" }}
                  />
                  <T variant="mdMed" sx={{ marginLeft: "2px" }}>
                    Profile & Settings
                  </T>
                </div>
                <div
                  className="flex flex-row"
                  style={{ alignItems: "flex-start", cursor: "pointer" }}
                  onClick={() => livechatActions.setVisibility("maximized")}
                >
                  <MessageOutlinedIcon
                    fontSize="small"
                    sx={{ alignSelf: "center" }}
                  />
                  <T variant="mdMed" sx={{ marginLeft: "2px" }}>
                    Contact Support
                  </T>
                </div>
              </div>
            </div>
            <LiveChatWidget
              // @ts-ignore
              license={process.env.REACT_APP_LIVECHAT_LICENSE}
              group={process.env.REACT_APP_LIVECHAT_GROUP}
              customerEmail={data.user.email}
              customName={`${data.user.first_name} ${data.user.last_name}`}
              visibility={livechatState.visibility}
              onVisibilityChanged={(visibility) =>
                livechatActions.setVisibility(visibility.visibility)
              }
            />
          </>
        ) : null}
      </div>
    </NavContainer>
  );
};

export default NavBar;
